import { render } from "preact";
import { useCallback, useEffect, useState } from "preact/hooks";
import { ApiError, loadSnapshot, logout, probeSession, type Snapshot } from "./api";
import { Dashboard } from "./Dashboard";
import { Login } from "./Login";
import "./styles.css";

/**
 * 진입점.
 *
 * 라우팅은 **해시**다 (`#/sandboxes`). 서버 라우트를 늘리지 않아도 되고(콘솔 자산은
 * 경로 허용목록으로만 서빙한다), 새로고침·뒤로가기가 그대로 동작한다. 로그인/대시보드
 * 분기는 여전히 URL 이 아니라 세션 유무다 — 로그인 흐름이 서버 리다이렉트(`/auth/login`
 * → Google → `/auth/callback` → `/`)라 클라이언트 라우팅이 낄 자리가 없다.
 *
 * 로그인 가능 여부는 **서버가 셸의 `<html data-sso>` 에 심는다.** 예전에 `HEAD
 * /auth/login` 프로브로 알아내려다 그 핸들러가 GET 전용이라 404 를 받아 버튼이 조용히
 * 숨었다 — 서버가 아는 것을 클라가 추측하면 안 된다. (빌드 타임 define 은 쓰지 않는다:
 * 같은 번들이 로그인 켠 배포와 끈 배포에 둘 다 실린다.)
 */
const SSO_ENABLED = document.documentElement.dataset["sso"] === "1";

const REFRESH_MS = 30_000;

/** `#/sandboxes` → `sandboxes`. 모르는 값은 개요로 떨어뜨린다. */
const VIEW_IDS = new Set(["", "sandboxes", "volumes", "workspaces", "keys"]);
function viewFromHash(): string {
  const id = location.hash.replace(/^#\/?/, "").split("/")[0] ?? "";
  return VIEW_IDS.has(id) ? id : "";
}

function App() {
  const [phase, setPhase] = useState<"probing" | "anon" | "signed">("probing");
  const [snap, setSnap] = useState<Snapshot | null>(null);
  const [stamp, setStamp] = useState("");
  const [error, setError] = useState<string | undefined>();
  const [view, setView] = useState(viewFromHash);

  const refresh = useCallback(async () => {
    try {
      const next = await loadSnapshot();
      setSnap(next);
      setStamp(new Date().toLocaleTimeString());
      setError(undefined);
    } catch (err) {
      // 세션이 만료되면 로그인 화면으로 되돌린다 — 에러 배너만 띄우면 사용자가
      // 무엇을 해야 하는지 알 수 없다.
      if (err instanceof ApiError && err.status === 401) {
        setPhase("anon");
        setSnap(null);
        return;
      }
      setError(err instanceof Error ? err.message : String(err));
    }
  }, []);

  useEffect(() => {
    void (async () => {
      const probe = await probeSession();
      if (!probe.ok) {
        setPhase("anon");
        return;
      }
      setPhase("signed");
      await refresh();
    })();
  }, [refresh]);

  useEffect(() => {
    const onHash = () => setView(viewFromHash());
    window.addEventListener("hashchange", onHash);
    return () => window.removeEventListener("hashchange", onHash);
  }, []);

  useEffect(() => {
    if (phase !== "signed") return;
    const id = setInterval(() => {
      // 창이 숨어 있으면 쉰다 — 열어둔 탭이 계속 API 를 두드릴 이유가 없다.
      if (!document.hidden) void refresh();
    }, REFRESH_MS);
    return () => clearInterval(id);
  }, [phase, refresh]);

  if (phase === "probing") return null;
  if (phase === "anon") return <Login ssoEnabled={SSO_ENABLED} />;
  return (
    <Dashboard
      snap={snap}
      view={view}
      stamp={stamp}
      error={error}
      /*
       * 세션은 샌드박스·볼륨 삭제만 할 수 있다 (컨트롤러의 operatorMayDelete). 그 사실을
       * 클라가 다시 판단하지 않고 신원 접두어로만 읽는다 — 진짜 집행은 서버에 있고 여기서
       * 하는 건 "누를 수 없는 버튼을 보여주지 않는" 것뿐이다. 그래서 이 판정이 틀려도
       * 권한이 새지 않는다 (403 이 온다).
       */
      canDelete={/^(session|dev):/.test(snap?.identity ?? "")}
      onRefresh={() => void refresh()}
      onLogout={async () => {
        await logout();
        location.reload();
      }}
    />
  );
}

const root = document.getElementById("root");
if (root) render(<App />, root);
